import React, { useState, useEffect } from 'react';
import { Search } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { useBookIdeas } from '../hooks/useBookIdeas';
import { useAuth } from '../hooks/useAuth';
import BookIdeaCard from '../components/BookIdeaCard';
import RoleSelector from '../components/landing/RoleSelector';
import ReaderLanding from '../components/landing/ReaderLanding';
import AuthorLanding from '../components/landing/AuthorLanding';
import FAQAccordion from '../components/landing/FAQAccordion';

function AuthenticatedHome() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const { ideas, loading, error } = useBookIdeas();

  useEffect(() => {
    const searchFromUrl = searchParams.get('search') || '';
    setSearchQuery(searchFromUrl);
  }, [searchParams]);

  const handleSearch = (value: string) => {
    setSearchQuery(value);
    if (value) {
      setSearchParams({ search: value });
    } else {
      setSearchParams({});
    }
  };

  const filteredIdeas = ideas.filter(idea => {
    const search = searchQuery.toLowerCase();
    return (
      idea.title.toLowerCase().includes(search) ||
      idea.description.toLowerCase().includes(search) ||
      idea.genre.some(g => g.toLowerCase().includes(search)) ||
      idea.identity_username.toLowerCase().includes(search) ||
      (idea.custom_tags && idea.custom_tags.some(t => t.toLowerCase().includes(search)))
    );
  });

  return (
    <div className="space-y-8">
      <header className="text-center"><br></br><br></br>
        <h1 className="text-5xl font-bold h1-Welcome">
          Welcome to StoryMuse.io
        </h1>
        <p className="mt-4 text-xl h1-Welcome-sub">
          Discover and shape the next great stories
        </p>
      </header>

      <div className="max-w-2xl mx-auto">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5" style={{ color: 'var(--color-accent2)' }} />
          <input
            type="text"
            placeholder="Search by title, description, genre, author, or tags..."
            value={searchQuery}
            onChange={(e) => handleSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:outline-none"
            style={{ 
              borderColor: 'var(--color-accent3)',
              backgroundColor: 'var(--color-background)',
              color: 'var(--color-font)',
              '--tw-ring-color': 'var(--color-accent1)'
            }}
          />
        </div>
      </div>
      
      {error && (
        <div className="max-w-2xl mx-auto p-4 border rounded-lg" 
             style={{ 
               backgroundColor: 'var(--color-accent3)', 
               borderColor: 'var(--color-accent1)',
               color: 'var(--color-font)'
             }}>
          {error}
        </div>
      )}

      {loading ? (
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 justify-items-center">
            {[...Array(6)].map((_, i) => (
              <div
                key={i}
                className="w-full max-w-md rounded-lg shadow-md h-[400px] animate-pulse"
                style={{ backgroundColor: 'var(--color-accent3)' }}
              />
            ))}
          </div>
        </div>
      ) : (
        <section className="max-w-7xl mx-auto px-4">
          {filteredIdeas.length > 0 ? (
            <div className="space-y-16">
              {/* In Progress Books */}
              <div>
                <h2 className="text-2xl font-bold mb-6" style={{ color: 'var(--color-font)' }}>
                  Stories in Progress
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 justify-items-center">
                  {filteredIdeas
                    .filter(idea => !idea.has_published_milestone)
                    .map((idea) => (
                      <div key={idea.id} className="w-full max-w-md">
                        <BookIdeaCard idea={idea} />
                      </div>
                    ))}
                </div>
              </div>

              {/* Published Books */}
              {filteredIdeas.some(idea => idea.has_published_milestone) && (
                <div>
                  <h2 className="text-2xl font-bold mb-6" style={{ color: 'var(--color-font)' }}>
                    Published Stories
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 justify-items-center">
                    {filteredIdeas
                      .filter(idea => idea.has_published_milestone)
                      .map((idea) => (
                        <div key={idea.id} className="w-full max-w-md">
                          <BookIdeaCard idea={idea} />
                        </div>
                      ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-12" style={{ color: 'var(--color-accent2)' }}>
              {searchQuery ? (
                <>No book ideas found matching your search.</>
              ) : (
                <>No book ideas have been posted yet. Be the first to share your story!</>
              )}
            </div>
          )}
        </section>
      )}
    </div>
  );
}

export default function Home() {
  const { user, loading } = useAuth();
  const [selectedRole, setSelectedRole] = useState<'reader' | 'author' | null>(null);

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[200px]">
        <div className="w-8 h-8 border-4 border-t-transparent rounded-full animate-spin"
             style={{ borderColor: 'var(--color-accent1)' }} />
      </div>
    );
  }

  if (!user) {
    if (!selectedRole) {
      return <RoleSelector onSelect={setSelectedRole} />;
    }

    return (
      <>
        {selectedRole === 'reader' ? <ReaderLanding /> : <AuthorLanding />}
        <FAQAccordion />
      </>
    );
  }

  return <AuthenticatedHome />;
}